import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  Upload,
  Download,
  Delete,
  Visibility,
  Warning,
  CheckCircle,
  FolderOpen,
  Description,
  PictureAsPdf,
  Image,
  Article,
  Search,
  FilterList,
  CalendarMonth,
  Person,
  Badge,
  Work,
  LocalHospital,
  School,
  Home,
  AccountBalance
} from '@mui/icons-material';
import api from '../services/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Documents = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    employee_id: '',
    document_type: '',
    document_name: '',
    issue_date: '',
    expiry_date: '',
    notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Document types with icons
  const documentTypes = {
    'passport': { name: 'Passport', icon: <Badge />, color: 'primary' },
    'id_card': { name: 'ID Card', icon: <Badge />, color: 'primary' },
    'work_permit': { name: 'Work Permit', icon: <Work />, color: 'warning' },
    'residence_permit': { name: 'Residence Permit', icon: <Home />, color: 'info' },
    'contract': { name: 'Employment Contract', icon: <Description />, color: 'success' },
    'medical': { name: 'Medical Certificate', icon: <LocalHospital />, color: 'error' },
    'education': { name: 'Education Certificate', icon: <School />, color: 'secondary' },
    'a1_form': { name: 'A1 Form', icon: <AccountBalance />, color: 'primary' },
    'bank': { name: 'Bank Details', icon: <AccountBalance />, color: 'default' },
    'other': { name: 'Other', icon: <FolderOpen />, color: 'default' }
  };

  useEffect(() => {
    fetchDocuments();
    fetchEmployees();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents');
      setDocuments(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.filter(emp => emp.status === 'active'));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDocumentStatus = (document) => {
    if (!document.expiry_date) {
      return { status: 'No Expiry', color: 'default', icon: null };
    }

    const today = new Date();
    const expiryDate = new Date(document.expiry_date);
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

    if (expiryDate < today) {
      return { status: 'Expired', color: 'error', icon: <Warning /> };
    }

    if (daysUntilExpiry <= 30) {
      return { status: `Expiring (${daysUntilExpiry} days)`, color: 'warning', icon: <Warning /> };
    }

    if (daysUntilExpiry <= 90) {
      return { status: `Valid (${daysUntilExpiry} days)`, color: 'info', icon: <CheckCircle /> };
    }

    return { status: 'Valid', color: 'success', icon: <CheckCircle /> };
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <Description />;
    if (mimeType.includes('pdf')) return <PictureAsPdf color="error" />;
    if (mimeType.includes('image')) return <Image color="primary" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <Article color="info" />;
    return <Description />;
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = searchTerm === '' || 
      doc.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || doc.document_type === filterType;
    
    const status = getDocumentStatus(doc);
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'valid' && status.color === 'success') ||
      (filterStatus === 'expiring' && status.color === 'warning') ||
      (filterStatus === 'expired' && status.color === 'error');
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const expiringDocuments = documents.filter(doc => {
    const status = getDocumentStatus(doc);
    return status.color === 'warning' || status.color === 'error';
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 
                           'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Allowed: PDF, JPG, PNG, DOC, DOCX');
        return;
      }
      
      setUploadFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadData.employee_id || !uploadData.document_type) {
      setError('Please fill all required fields');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('employee_id', uploadData.employee_id);
    formData.append('document_type', uploadData.document_type);
    formData.append('document_name', uploadData.document_name || uploadFile.name);
    formData.append('issue_date', uploadData.issue_date);
    formData.append('expiry_date', uploadData.expiry_date);
    formData.append('notes', uploadData.notes);

    try {
      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess('Document uploaded successfully!');
      setUploadDialog(false);
      setUploadFile(null);
      setUploadData({
        employee_id: '',
        document_type: '',
        document_name: '',
        issue_date: '',
        expiry_date: '',
        notes: ''
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Failed to upload document');
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await api.delete(`/documents/${documentId}`);
        setSuccess('Document deleted successfully');
        fetchDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
        setError('Failed to delete document');
      }
    }
  };

  // Statistics
  const statistics = {
    total: documents.length,
    expiring: expiringDocuments.filter(d => getDocumentStatus(d).color === 'warning').length,
    expired: expiringDocuments.filter(d => getDocumentStatus(d).color === 'error').length,
    byType: Object.keys(documentTypes).map(type => ({
      type,
      count: documents.filter(d => d.document_type === type).length
    }))
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Document Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Upload />}
          onClick={() => setUploadDialog(true)}
        >
          Upload Document
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderOpen color="primary" />
                <Box>
                  <Typography variant="h6">{statistics.total}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Documents
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" />
                <Box>
                  <Typography variant="h6">{statistics.expiring}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expiring Soon
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="error" />
                <Box>
                  <Typography variant="h6">{statistics.expired}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expired
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography variant="h6">
                    {statistics.total - statistics.expiring - statistics.expired}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valid
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Documents" />
          <Tab label="Expiring Soon" />
          <Tab label="By Employee" />
          <Tab label="By Type" />
        </Tabs>
      </Paper>

      {/* All Documents Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            sx={{ minWidth: 150 }}
            label="Document Type"
          >
            <MenuItem value="all">All Types</MenuItem>
            {Object.entries(documentTypes).map(([key, type]) => (
              <MenuItem key={key} value={key}>{type.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{ minWidth: 150 }}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="valid">Valid</MenuItem>
            <MenuItem value="expiring">Expiring Soon</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </TextField>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading documents...
                  </TableCell>
                </TableRow>
              ) : filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No documents found
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((document) => {
                  const status = getDocumentStatus(document);
                  const docType = documentTypes[document.document_type] || documentTypes.other;
                  
                  return (
                    <TableRow key={document.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFileIcon(document.mime_type)}
                          <Typography variant="body2">
                            {document.document_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={docType.icon}
                          label={docType.name}
                          size="small"
                          color={docType.color}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => navigate(`/admin/employees/${document.employee_id}`)}
                        >
                          {document.employee_first_name} {document.employee_last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(document.issue_date)}</TableCell>
                      <TableCell>{formatDate(document.expiry_date)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {status.icon}
                          <Chip
                            label={status.status}
                            color={status.color}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{formatFileSize(document.file_size)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(document.id, document.file_name)}
                          title="Download"
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/documents/${document.id}`)}
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(document.id)}
                          title="Delete"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Expiring Soon Tab */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Documents expiring within the next 30 days or already expired
        </Alert>
        
        <Grid container spacing={2}>
          {expiringDocuments.map((document) => {
            const status = getDocumentStatus(document);
            const docType = documentTypes[document.document_type] || documentTypes.other;
            
            return (
              <Grid item xs={12} md={6} key={document.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {docType.icon}
                          <Typography variant="subtitle1" fontWeight="bold">
                            {docType.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {document.document_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Employee: {document.employee_first_name} {document.employee_last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expires: {formatDate(document.expiry_date)}
                        </Typography>
                      </Box>
                      <Chip
                        label={status.status}
                        color={status.color}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDownload(document.id, document.file_name)}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setUploadDialog(true)}
                      >
                        Update
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* By Employee Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="body1" color="text.secondary">
          Document organization by employee - Coming soon
        </Typography>
      </TabPanel>

      {/* By Type Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          {statistics.byType.map(({ type, count }) => {
            const docType = documentTypes[type];
            return (
              <Grid item xs={12} md={4} key={type}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ fontSize: 40 }}>
                        {docType.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6">{count}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {docType.name}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Employee"
                value={uploadData.employee_id}
                onChange={(e) => setUploadData({ ...uploadData, employee_id: e.target.value })}
                required
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name} ({employee.personal_code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Document Type"
                value={uploadData.document_type}
                onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                required
              >
                {Object.entries(documentTypes).map(([key, type]) => (
                  <MenuItem key={key} value={key}>{type.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Name"
                value={uploadData.document_name}
                onChange={(e) => setUploadData({ ...uploadData, document_name: e.target.value })}
                placeholder="Optional - defaults to file name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Issue Date"
                type="date"
                value={uploadData.issue_date}
                onChange={(e) => setUploadData({ ...uploadData, issue_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={uploadData.expiry_date}
                onChange={(e) => setUploadData({ ...uploadData, expiry_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<Upload />}
              >
                {uploadFile ? uploadFile.name : 'Select File'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
              </Button>
              {uploadFile && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Size: {formatFileSize(uploadFile.size)}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={uploadData.notes}
                onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!uploadFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Documents;
